import React, { useState, useEffect } from 'react';
import { Form, Row, Col, Card, Button } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import SelectInput from '../../../SelectInput/SelectInput';
import { Editor } from '@tinymce/tinymce-react';
import SendEmailTestModal from '../../../Modals/SendEmailTestModal/SendEmailTestModal';
import PasteRecipientsModal from '../../../Modals/PasteRecipientsModal/PasteRecipientsModal';
import EmailPreviewModal from '../../../Modals/EmailPreviewModal/EmailPreviewModal';
import isEmail from 'validator/lib/isEmail';


function EmailSettingsForm(props) {
    const { loading, email, saving, saveEmail, sendEmail, isNew, isEnabled } = props;
    const { t } = useTranslation();
    const onSave = () => {
        _mapPartnerRecipients();
        saveEmail(values);
    }
    const onSend = (values) => sendEmail(values);

    const [ modalShow, setModalShow ] = useState(false);
    const [ recipients, setRecipients] = useState(false);
    const [ disabled, setDisabled ] = useState(false);
    const [showPasteRecipients, setShowPasteRecipients] = useState(false);
    const [showEmailPreview, setShowEmailPreview] = useState(false);
    
    const ValidationSchema = Yup.object().shape({
        title: Yup.string().required(t('form.title_required')),
        senderName: Yup.string().required(t('form.sender_name_required'))
    
    });
    const formik = useFormik({
        initialValues: {...email},
        validationSchema: ValidationSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            _mapPartnerRecipients();
            onSend(values);
        }
    });

    const { errors, touched, values, setValues } = formik;

    useEffect(() => {
        // Set recipients if none are added yet.
        if(!recipients) {
             // Map label and name with receiverEmail so select shows the email.
            email.partnerEmailRecipients.map(r => {
                r.label = r.receiverEmail;
                r.name = r.receiverEmail;
                return r;
            })
            setRecipients(email.partnerEmailRecipients);
        }
        email.status !== 'draft' && setDisabled(true);
    }, [email, recipients, values]);

    // validator for SelectInput to check for valid emails when adding manually new recipients
    const _validateRecipientAdd = (createdOption) => {
        /* const emailRegex = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/gm; // eslint-disable-line no-useless-escape
        return createdOption.match(emailRegex); */
        return isEmail(createdOption);
    };

    const _onRecipientsInputSelect = (selected, action) => {
        let mappedSelected = selected ? selected.map(s => ({...s, receiverEmail: s.value})) : [];
        setRecipients(mappedSelected);
    };

    const _handleContentHtmlChange = contentHtml => setValues({...values, contentHtml: contentHtml});

    const _mapPartnerRecipients = () => {
        email.partnerEmailRecipients.map(v => {
            // If recipient can't be found from the current participants, set timeDeleted so we remove it. 
            if(recipients.filter(r => v.partnerEmailRecipientId === r.partnerEmailRecipientId).length === 0) {
                v.timeDeleted = true;
            }
            return v;
        });
        values.partnerEmailRecipients = [...email.partnerEmailRecipients, ...recipients.filter(r => !r.partnerEmailRecipientId)];
    }

    const _onRecipientsPaste = (pasted) => {
        const filteredPasted = [...pasted].filter(p => recipients.map(r => r.receiverEmail).indexOf(p) === -1);

        filteredPasted.forEach(recipient => {
            if (!values.partnerEmailRecipients.some(r => r.receiverEmail === recipient)) {
                values.partnerEmailRecipients = [...values.partnerEmailRecipients, { receiverEmail: recipient, label: recipient, name: recipient }];
            }
        })

        setRecipients([...recipients, ...values.partnerEmailRecipients]);
    }

    const onKeyPress = (event) => {
        if (event.which === 13 /* Enter */) {
          event.preventDefault();
        }
    }

    return (
        <div>
            <Form onKeyPress={onKeyPress} onSubmit={e => {e.preventDefault(); formik.handleSubmit()}}>
                <Form.Group as={Row} controlId="title" className="required">
                    <Form.Label column sm={3}>{t('emails.title')}</Form.Label>
                    <Col sm={9}>
                        <Form.Control
                            value={values.title || ''}
                            onChange={formik.handleChange}
                            disabled={disabled}
                            isInvalid={errors.title && touched.title}
                            required
                        />
                        {errors.title && touched.title ? (
                            <Form.Control.Feedback type="invalid">
                                {errors.title}
                            </Form.Control.Feedback>
                        ) : null}
                    </Col>
                </Form.Group>
                <Form.Group as={Row} controlId="senderName" className="required">
                    <Form.Label column sm={3}>{t('emails.sender_name')}</Form.Label>
                    <Col sm={9}>
                        <Form.Control
                            value={values.senderName || ''}
                            onChange={formik.handleChange}
                            disabled={disabled}
                            isInvalid={errors.senderName && touched.senderName}
                            required
                        />
                        {errors.senderName && touched.senderName ? (
                            <Form.Control.Feedback type="invalid">
                                {errors.senderName}
                            </Form.Control.Feedback>
                        ) : null}
                    </Col>
                </Form.Group>
                <Form.Group as={Row} controlId="recipients" className="required">
                    <Form.Label column sm={3}>{t('emails.recipients')}</Form.Label>
                    <Col sm={9}>
                        <SelectInput
                            components={{DropdownIndicator: null}}
                            invertBackground
                            isMulti
                            loading={loading}
                            options={recipients}
                            defaultOptions={recipients}
                            placeholder={t('emails.add_recipients_placeholder')}
                            disabled={disabled}
                            asCreatable
                            onlyCreate
                            onChange={_onRecipientsInputSelect}
                            validateCreatedOption={_validateRecipientAdd}
                        />
                        <Button type="button" style={{marginTop: '15px'}} onClick={() => {setShowPasteRecipients(true)}} variant="secondary" disabled={(loading || saving || disabled)}>
                            {t('emails.paste_recipients')}
                        </Button>
                    </Col>
                    
                </Form.Group>
                <Form.Group as={Row} controlId="messageBody">
                    <Form.Label column sm={3}>{t('emails.message_body')}</Form.Label>
                    <Col sm={9}>
                        <Editor
                            id="description"
                            textareaName="description"
                            value={values.contentHtml}
                            apiKey="f7xl8ix2ex377l8mbg0dgj4sy3nv3pyn27n7ulaghwsz2l52"
                            disabled={disabled}
                            init={{
                                height: 400,
                                paste_word_valid_elements: "p,a,b,strong,i,em,h1,h2",
                                menubar: false,
                                plugins: [
                                    'advlist autolink lists link image charmap print preview anchor',
                                    'searchreplace visualblocks code fullscreen',
                                    'insertdatetime media table paste code wordcount'
                                ],
                                toolbar: 'formlink | formatselect | bold italic underline | alignleft aligncenter alignright alignjustify | bullist numlist | link unlink image | paste code | undo redo ',
                                block_formats: 'Paragraph=p;Heading 1=h1;Heading 2=h2;Heading 3=h3;Heading 4=h4;',
                                setup: (editor) => {
                                    editor.ui.registry.addButton('formlink', {
                                        text: t('emails.registration_form_link'),
                                        onAction: () => {
                                            editor.insertContent('{registrationFormUrl}');
                                        }
                                    })
                                }
                            }}
                            onEditorChange={_handleContentHtmlChange}
                        />
                    </Col>
                </Form.Group>
                <Card.Footer>
                    <Form.Group as={Row}>
                        <Col sm={{span: 9, offset: 3}}>
                            <Button type="button" onClick={onSave} style={{marginRight: '20px'}} variant="success" disabled={(loading || saving || disabled)}>
                                {isNew ? t('general.save') : t('general.save_changes')}
                            </Button>
                            {!isNew ?
                                <span>
                                    <Button
                                        type="button"
                                        onClick={() => {setModalShow(true)}}
                                        style={{marginRight: '20px'}}
                                        variant="secondary"
                                        disabled={(loading || saving || disabled || !isEnabled)}
                                    >
                                        {t('emails.send_email_test')}
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="secondary"
                                        disabled={(loading || saving || disabled || !isEnabled)}
                                        style={{marginRight: '20px'}}
                                    >
                                        {t('emails.send_email')}
                                    </Button>
                                    <Button
                                        type="button"
                                        onClick={() => setShowEmailPreview(true)}
                                        variant="secondary"
                                    >
                                        {t('emails.preview')}
                                    </Button>
                                </span>
                            : null}
                        </Col>
                    </Form.Group>
                </Card.Footer>
                
            </Form>
            {showPasteRecipients &&
                <PasteRecipientsModal
                    setRecipients={_onRecipientsPaste}
                    handleClose={() => setShowPasteRecipients(false)}
                />
            }
            <SendEmailTestModal
                show={modalShow}
                email={values}
                disabled={disabled}
                onHide={() => setModalShow(false)}
            />

            {showEmailPreview ?
                <EmailPreviewModal
                    partnerEmailId={email.partnerEmailId}
                    onHide={() => setShowEmailPreview(false)}
                />
            : null}
        </div>
    )
}

export default EmailSettingsForm;